// Cross-tenant bereavement/support-pathway analytics — BesideU Admin.
//
// Pooled across every organization; no per-org breakdown, no Amber/Red
// safety-classifier data (see PROJECT.md "Cross-tenant analytics" for the
// scope rationale). Presentation-only — it renders exactly the counts the
// server computed and never invents its own thresholds, mirroring the
// posture of `AmberAggregateTile.tsx`/`EngagementAggregateTile.tsx`.
//
// **Production gated on CGC/legal sign-off** (PROJECT.md "Remaining work —
// non-engineering") — engineering-complete, not yet cleared to demo/enable
// per the pilot's governance process.

import { useQuery } from '@tanstack/react-query';
import type {
  AdminAnalyticsSummary,
  CrossTenantAggregateBlock,
  CrossTenantEngagementBlock,
} from '@besideu/shared/admin-analytics';
import { api } from '@/lib/api.ts';
import { MetricCard } from '@/components/ui/MetricCard.tsx';

export function AdminAnalytics() {
  const query = useQuery({
    queryKey: ['admin', 'analytics'],
    queryFn: () => api.get<{ analytics: AdminAnalyticsSummary }>('/api/admin/analytics'),
  });

  const header = (
    <header>
      <h1 className="text-2xl font-semibold tracking-tight">Analytics</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Pooled bereavement + support-pathway signals across every organisation. Counts only — no
        per-org breakdown, no case content. Production gated on CGC/legal sign-off.
      </p>
    </header>
  );

  if (query.isPending) {
    return (
      <section className="space-y-10">
        {header}
        <p className="text-sm text-muted-foreground">Loading…</p>
      </section>
    );
  }
  if (query.error || !query.data) {
    return (
      <section className="space-y-10">
        {header}
        <p className="text-sm text-traffic-red">Could not load analytics.</p>
      </section>
    );
  }

  return (
    <section className="space-y-10">
      {header}
      <AnalyticsBody data={query.data.analytics} />
    </section>
  );
}

function AnalyticsBody({ data }: { data: AdminAnalyticsSummary }) {
  return (
    <>
      <SectionHeader
        title="Bereavement volume"
        subtitle="Number of companies with a bereavement, current and cumulative."
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Active orgs with an open case"
          value={data.orgVolume.activeOrgsWithOpenCase}
        />
        <MetricCard label="Orgs with any case ever" value={data.orgVolume.orgsWithAnyCaseEver} />
        <MetricCard label="Open cases" value={data.orgVolume.totalOpenCases} />
        <MetricCard label="Closed cases" value={data.orgVolume.totalClosedCases} />
      </div>
      <MonthlyTrend
        rows={data.orgVolume.casesCreatedByMonth}
        suppressed={data.orgVolume.monthlyTrendSuppressed}
        threshold={data.orgVolume.monthlyTrendThreshold}
      />

      <SectionHeader
        title="Loss-type breakdown"
        subtitle="Open + quiet cases, pooled across every org. Sub-floor types are rolled into 'Other'."
      />
      <AggregateBlockCard block={data.lossTypeBreakdown} />

      <SectionHeader
        title="Support-pathway completion"
        subtitle="Touch-point workload + adherence, pooled across every org's open cases."
      />
      {data.pathwayCompletion.suppressed ? (
        <SuppressedNote
          threshold={data.pathwayCompletion.threshold}
          subject="evaluable touch points"
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <MetricCard label="Due this week" value={data.pathwayCompletion.touchPointsDueThisWeek} />
          <MetricCard label="Overdue" value={data.pathwayCompletion.overdueTouchPoints} />
          <MetricCard
            label="Completion rate"
            value={formatRate(
              data.pathwayCompletion.completion.met,
              data.pathwayCompletion.completion.evaluable,
            )}
            sublabel={`${data.pathwayCompletion.completion.met} of ${data.pathwayCompletion.completion.evaluable} evaluable`}
          />
        </div>
      )}

      <SectionHeader
        title="Engagement"
        subtitle="Support Space Activated + Resources Accessed, pooled across every org's cases."
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <EngagementCard
          label="Support space activated"
          block={data.engagement.supportSpaceActivated}
        />
        <EngagementCard label="Resources accessed" block={data.engagement.resourcesAccessed} />
      </div>

      <SectionHeader
        title="Framework version distribution"
        subtitle="Which framework version each open/closed case was stamped with at creation."
      />
      <FrameworkVersionTable rows={data.frameworkVersionDistribution} />
    </>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div>
      <h2 className="text-lg font-semibold">{title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
    </div>
  );
}

function AggregateBlockCard({ block }: { block: CrossTenantAggregateBlock }) {
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <p className="text-3xl font-semibold tabular-nums">{block.total}</p>
      <p className="mt-1 text-sm font-medium text-muted-foreground">
        total open + quiet cases counted in this breakdown
      </p>
      {block.suppressed ? (
        <SuppressedNote threshold={block.threshold} subject="loss-type categories" />
      ) : (
        <ul className="mt-3 divide-y divide-border">
          {block.categories.map((c) => (
            <li key={c.label} className="flex items-baseline justify-between gap-2 py-1.5 text-sm">
              <span>{c.label}</span>
              <span className="tabular-nums">{c.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EngagementCard({ label, block }: { label: string; block: CrossTenantEngagementBlock }) {
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <p className="text-3xl font-semibold tabular-nums">
        {block.suppressed ? '—' : formatRate(block.total, block.totalCases)}
      </p>
      <p className="mt-1 text-sm font-medium">{label}</p>
      {block.suppressed ? (
        <SuppressedNote threshold={block.threshold} subject="open cases" />
      ) : (
        <p className="mt-1 text-xs text-muted-foreground">
          {block.total} of {block.totalCases} open cases
        </p>
      )}
    </div>
  );
}

function MonthlyTrend({
  rows,
  suppressed,
  threshold,
}: {
  rows: ReadonlyArray<{ month: string; count: number }>;
  suppressed: boolean;
  threshold: number;
}) {
  if (suppressed) return <SuppressedNote threshold={threshold} subject="the monthly trend" />;
  if (rows.length === 0) return null;
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground uppercase">
            <th className="px-4 py-2 font-medium">Month</th>
            <th className="px-4 py-2 font-medium">Cases created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <tr key={r.month}>
              <td className="px-4 py-2">{r.month}</td>
              <td className="px-4 py-2 tabular-nums">{r.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FrameworkVersionTable({
  rows,
}: {
  rows: ReadonlyArray<{ version: string; count: number }>;
}) {
  if (rows.length === 0)
    return <p className="text-sm text-muted-foreground">No cases recorded yet.</p>;
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground uppercase">
            <th className="px-4 py-2 font-medium">Version</th>
            <th className="px-4 py-2 font-medium">Cases</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <tr key={r.version}>
              <td className="px-4 py-2 font-mono">{r.version}</td>
              <td className="px-4 py-2 tabular-nums">{r.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SuppressedNote({ threshold, subject }: { threshold: number; subject: string }) {
  return (
    <p className="mt-2 text-xs text-muted-foreground">
      Pool below the privacy floor of {threshold} — {subject} are not broken out for privacy.
    </p>
  );
}

function formatRate(numerator: number, denominator: number): string {
  if (denominator === 0) return '—';
  return `${Math.round((numerator / denominator) * 100)}%`;
}
